import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { GlobalStyles } from './styles/GlobalStyles'
import { theme } from './styles/theme'
import { MainLayout } from './components/layout/MainLayout'
import { LoginPage } from './pages/Login/LoginPage'
import { ProfilePage } from './pages/Profile/ProfilePage'
import { HomePage } from './pages/Home/HomePage'
import { TimelinePage } from './pages/Timeline/TimelinePage'
import { EstadisticasPage } from './pages/Estadisticas/EstadisticasPage'
import { OportunidadesPage } from './pages/Oportunidades/OportunidadesPage'
import { MatrizConocimientosPage } from './pages/MatrizConocimientos/MatrizConocimientosPage'
import { MatrizOportunidadesPage } from './pages/MatrizOportunidades/MatrizOportunidadesPage'
import { AdminPage } from './pages/Admin/AdminPage'
import { InfrastructurePage } from './pages/Infrastructure/InfrastructurePage'
import { LinkedInFeedPage } from './pages/LinkedIn'
import { ImputacionPage } from './pages/Imputacion/ImputacionPage'
import PracticasSetup from './pages/PracticasSetup'
import { Spinner } from './components/shared/StyledComponents'
import styled from 'styled-components'
import { OPPORTUNITIES_PATH, toOpportunityId, type OpenOpportunityState } from './utils/openOpportunity'

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`

interface ProtectedRouteProps {
    children: React.ReactNode
    adminOnly?: boolean
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, adminOnly = false }) => {
    const { isAuthenticated, isLoading, user, needsPracticasSetup, needsProfileSetup } = useAuth()
    const location = useLocation()

    if (isLoading) {
        return (
            <LoadingContainer>
                <Spinner />
            </LoadingContainer>
        )
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    // If intern needs prácticas setup, redirect to setup page
    // But don't redirect if already on the setup page
    if (needsPracticasSetup && location.pathname !== '/practicas-setup') {
        return <Navigate to="/practicas-setup" replace />
    }

    // If admin (gerente) needs to fill their profile, redirect to /profile
    // But don't redirect if already on the profile page
    if (needsProfileSetup && location.pathname !== '/profile') {
        return <Navigate to="/profile" replace />
    }

    const isAdmin = user?.role?.toLowerCase() === 'admin'

    // If route is admin-only and user is not admin, redirect to profile
    if (adminOnly && !isAdmin) {
        return <Navigate to="/profile" replace />
    }

    return <>{children}</>
}

const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading, user, needsProfileSetup } = useAuth()

    if (isLoading) {
        return (
            <LoadingContainer>
                <Spinner />
            </LoadingContainer>
        )
    }

    if (isAuthenticated) {
        const isAdmin = user?.role?.toLowerCase() === 'admin'
        // Non-admin users always go to profile page first
        if (!isAdmin) {
            return <Navigate to="/profile" replace />
        }
        // Admin (gerente) without profile filled must go to /profile first
        if (needsProfileSetup) {
            return <Navigate to="/profile" replace />
        }
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

/**
 * /oportunidades/:id is a bookmarkable alias for the navigation contract in
 * utils/openOpportunity: OportunidadesPage only reads location.state, so the
 * id is moved there. Without this route the catch-all sent such links to
 * /profile. It sits behind adminOnly like /oportunidades itself.
 */
export const OpportunityDeepLink: React.FC = () => {
    const { id } = useParams()
    const oppId = toOpportunityId(id)
    const state: OpenOpportunityState | undefined = oppId ? { openOpportunityId: oppId } : undefined
    return <Navigate to={OPPORTUNITIES_PATH} replace state={state} />
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />

            {/* Prácticas setup page for interns */}
            <Route
                path="/practicas-setup"
                element={
                    <ProtectedRoute>
                        <PracticasSetup />
                    </ProtectedRoute>
                }
            />

            {/* Main app layout - accessible by all authenticated users */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <MainLayout />
                    </ProtectedRoute>
                }
            >
                {/* Admin-only routes */}
                <Route index element={<ProtectedRoute adminOnly><HomePage /></ProtectedRoute>} />
                <Route path="estadisticas" element={<ProtectedRoute adminOnly><EstadisticasPage /></ProtectedRoute>} />
                <Route path="oportunidades" element={<ProtectedRoute adminOnly><OportunidadesPage /></ProtectedRoute>} />
                <Route path="oportunidades/:id" element={<ProtectedRoute adminOnly><OpportunityDeepLink /></ProtectedRoute>} />
                <Route path="admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
                <Route path="profile/:userId" element={<ProtectedRoute adminOnly><ProfilePage /></ProtectedRoute>} />

                {/* Routes accessible to all users (with filtered data for non-admin) */}
                <Route path="timeline" element={<TimelinePage />} />
                {/* Imputación: own month for everyone; the "Equipo" tab inside is admin-only */}
                <Route path="imputacion" element={<ImputacionPage />} />
                <Route path="infraestructura" element={<InfrastructurePage />} />
                <Route path="matriz-conocimientos" element={<MatrizConocimientosPage />} />
                <Route path="matriz-oportunidades" element={<MatrizOportunidadesPage />} />

                {/* SOTA feed: all users; assignments are an admin-only tab inside the page */}
                <Route path="sota" element={<LinkedInFeedPage />} />
                {/* Legacy alias: /linkedin → /sota */}
                <Route path="linkedin" element={<Navigate to="/sota" replace />} />

                {/* Profile page for current user - accessible by all */}
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Catch all - redirect to profile */}
            <Route path="*" element={<Navigate to="/profile" replace />} />
        </Routes>
    )
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <GlobalStyles />
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </BrowserRouter>
        </ThemeProvider>
    )
}

export default App
